import { useStore } from '@nanostores/react';
import { toast } from 'react-toastify';
import { Checkbox } from '~/components/ui/Checkbox';
import { LoadingDots } from '~/components/ui/LoadingDots';
import { MemberRow } from '~/components/ui/MembersTable/MemberRow';
import { MemberSelectionToolbar } from '~/components/ui/MembersTable/MemberSelectionToolbar';
import { SendInviteField } from '~/components/ui/MembersTable/SendInviteField';
import { Pagination } from '~/components/ui/Pagination';
import { Table, TableHead, TableRow } from '~/components/ui/Table';
import { usePaginatedData } from '~/lib/hooks/usePaginatedData';
import { teamStore } from '~/lib/stores/team';
import { userStore } from '~/lib/stores/user';
import { type OrganizationData, type OrganizationUserRoles } from '~/types/organization';
import { classNames } from '~/utils/classNames';

interface TeamMembersTableProps {
  organization: OrganizationData;
  refetchMembers: VoidFunction;
  type: 'admin-view' | 'member-view';
  pageSize?: number;
  isOnboarding?: boolean;
}

export const MembersTable = ({
  organization,
  refetchMembers,
  type,
  pageSize = 5,
  isOnboarding = false,
}: TeamMembersTableProps) => {
  const user = useStore(userStore.user);
  const members = useStore(teamStore.members);
  const isLoadingTeam = useStore(teamStore.isLoadingTeam);
  const selectedMembers = useStore(teamStore.selectedMembers);
  const isAllSelected = useStore(teamStore.isAllSelected);

  const isCustomProvider = organization?.provider === 'custom';
  const isReadonlyView = !user?.featureFlags.teamManagement || type === 'member-view' || !isCustomProvider;

  const {
    data: paginatedMembers,
    page,
    onPageChange,
    totalPages,
  } = usePaginatedData(members, {
    pageSize,
  });

  const isSelfSelected = (username: string | undefined, selected: string[]) =>
    !!username && selected.includes(username);

  const handleRolesChange = (role: OrganizationUserRoles) => {
    if (isSelfSelected(user?.username, selectedMembers)) {
      toast.error(`You can't update your own role`);
      return;
    }

    teamStore.changeRoles(selectedMembers, organization, { newRole: role });
  };

  const Header = () => (
    <>
      {!isReadonlyView && selectedMembers.length > 0 ? (
        <MemberSelectionToolbar
          selectedCount={selectedMembers.length}
          onRolesChange={handleRolesChange}
          organization={organization}
        />
      ) : !isReadonlyView ? (
        <SendInviteField refetchMembers={refetchMembers} organization={organization} currentMembers={members} />
      ) : null}
    </>
  );

  if (!user) {
    return null;
  }

  return (
    <div className="w-full flex flex-col gap-4">
      <Header />
      <Table className="w-full">
        <TableHead
          className={classNames(
            'grid items-center gap-4 text-xs text-bolt-ds-text-tertiary',
            !isReadonlyView ? 'grid-cols-24' : 'grid-cols-20',
          )}
        >
          {!isReadonlyView && (
            <div className="col-span-1">
              <Checkbox
                disabled={members.length === 1}
                checked={isAllSelected ? true : selectedMembers.length === 0 ? false : 'indeterminate'}
                onChange={() => {
                  teamStore.selectAllMembers();
                }}
              />
            </div>
          )}
          <div className="col-span-9 truncate pl-1">Member</div>
          <div className="col-span-5 text-end truncate">Rights</div>
          <div className="col-span-6 text-center truncate">Subscription</div>
          {!isReadonlyView && <div className="col-span-3 text-center truncate">Action</div>}
        </TableHead>
        {isLoadingTeam ? (
          <TableRow className="w-full text-sm text-bolt-ds-textTertiary bg-bolt-ds-surface">
            <LoadingDots text="Loading members..." />
          </TableRow>
        ) : paginatedMembers.length ? (
          paginatedMembers.map((member) => (
            <MemberRow
              refetchMembers={refetchMembers}
              key={member.id}
              isReadonlyView={isReadonlyView}
              organization={organization}
              member={member}
              user={user}
              isOnboarding={isOnboarding}
              isChecked={selectedMembers.includes(member.user.username)}
              selectMember={(username: string) => {
                teamStore.selectMember(username);
              }}
            />
          ))
        ) : (
          <TableRow className="w-full text-sm text-bolt-ds-textTertiary">No members</TableRow>
        )}
      </Table>
      {!isLoadingTeam && members.length > pageSize && (
        <Pagination
          page={page}
          totalPages={totalPages}
          onPageChange={onPageChange}
          className="rounded-t-[0px] mt-[-16px] border-t-transparent"
        />
      )}
    </div>
  );
};
